import { useState, type FC } from "react";
import {
  Box,
  Card,
  Checkbox,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import { useTranslation } from "react-i18next";
import { Player } from "@/shared/components/Player";
import {
  getStreamSignalProblem,
  minutesSince,
  useContentStreamStatusChangedSubscription,
  useNow,
  type StreamsItem,
} from "@/partner/entities/streams";
import { useDeviceBreakpoint } from "@/partner/shared/hooks";
import { ItemTitleLink } from "@/partner/shared/components/ItemTitleLink";
import { PagePaths } from "@/partner/shared/constants";
import { PremiumBadge } from "@/partner/shared/ui";
import { StreamStatusType } from "@/types/graphql/partner.generated";
import {
  getStreamListBadge,
  STREAM_LIST_BADGE_COLORS,
  STREAM_LIST_BADGE_LABEL_KEYS,
} from "../domains";

const TITLE_MAX_LENGTH = 200;
const TITLE_MAX_LENGTH_MOBILE = 60;

function truncate(text: string, maxLength: number): string {
  return text.length > maxLength ? `${text.slice(0, maxLength - 1)}…` : text;
}

interface StreamsListItemProps {
  item: StreamsItem;
  selected: boolean;
  onSelectOneChange: (
    id: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onClickItem: (id: string) => void;
}

export const StreamsListItem: FC<StreamsListItemProps> = ({
  item,
  selected,
  onSelectOneChange,
  onClickItem,
}) => {
  const { t } = useTranslation();
  const isMobile = useDeviceBreakpoint("md");
  const status = item.stream?.status ?? StreamStatusType.PendingType;
  const isLive = status === StreamStatusType.StartedType;
  const liveBadge = getStreamListBadge(item);
  const [isHovered, setIsHovered] = useState(false);
  // Идущий эфир слушает подписку статуса: смена сигнала приходит в неё же
  // и через нормализованный кеш Apollo обновляет строку без перезапроса.
  useContentStreamStatusChangedSubscription({
    variables: { id: item.id },
    skip: !isLive,
  });
  const signalProblem = item.stream
    ? getStreamSignalProblem(item.stream)
    : null;
  const now = useNow(signalProblem === "NO_VIDEO");
  let signalLabel: string | null = null;
  if (signalProblem === "DISCONNECTED") {
    signalLabel = t("streams.signal.listDisconnected");
  } else if (signalProblem === "NO_VIDEO" && item.stream?.signal) {
    const minutes = minutesSince(item.stream.signal.since, now);
    signalLabel =
      minutes > 0
        ? t("streams.signal.listNoVideo", { count: minutes })
        : t("streams.signal.listNoVideoJustNow");
  }
  const showLivePreview =
    isHovered &&
    isLive &&
    item.stream?.liveConnected &&
    !!item.stream?.playback?.hlsUrl;

  return (
    <Card
      elevation={0}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        py: 2,
        px: 1,
        cursor: "pointer",
        opacity: item.publishAt ? 1 : 0.6,
        bgcolor: selected ? "background.selected" : "background.paper",
        // Идущий эфир без видео подсвечен, чтобы его было видно в списке.
        ...(signalProblem && {
          bgcolor: (theme) => alpha(theme.palette.error.main, 0.12),
          outline: 1,
          outlineColor: "error.main",
        }),
      }}
      onClick={() => onClickItem(item.id)}
    >
      <Checkbox
        checked={selected}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => onSelectOneChange(item.id, e)}
      />

      <Box
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          position: "relative",
          width: { xs: 160 / 1.5, sm: 160 },
          height: { xs: 90 / 1.5, sm: 90 },
          flexShrink: 0,
          borderRadius: 1,
          overflow: "hidden",
          bgcolor: "action.hover",
        }}
      >
        {showLivePreview && item.stream?.playback?.hlsUrl ? (
          <Player
            src={item.stream.playback.hlsUrl}
            poster={item.stream.playback.thumbnailUrl ?? undefined}
            autoPlay
            muted
            controls={false}
            hotkeys={false}
          />
        ) : (
          <>
            {item.preview?.url && (
              <img
                src={item.preview.url}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            )}
            {item.premium && (
              <PremiumBadge title={t("streams.item.premiumTooltip")} />
            )}
            {liveBadge && (
              <Chip
                size="small"
                color={STREAM_LIST_BADGE_COLORS[liveBadge]}
                label={t(STREAM_LIST_BADGE_LABEL_KEYS[liveBadge])}
                sx={{ position: "absolute", top: 4, right: 4 }}
              />
            )}
          </>
        )}
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <ItemTitleLink
          to={`${PagePaths.streams}?tab=edit&id=${item.id}`}
          onOpen={() => onClickItem(item.id)}
          underline="none"
          variant="h6"
          color="text.primary"
          sx={{
            display: "block",
            fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.2rem" },
          }}
        >
          {truncate(
            item.title,
            isMobile ? TITLE_MAX_LENGTH_MOBILE : TITLE_MAX_LENGTH
          )}
        </ItemTitleLink>
        {signalLabel && (
          <Chip
            size="small"
            color="error"
            icon={<VideocamOffIcon />}
            label={signalLabel}
            sx={{ mt: 0.5 }}
          />
        )}
        {item.tags.length > 0 && !isMobile && (
          <Stack
            direction="row"
            spacing={0.5}
            sx={{ mt: 0.5, flexWrap: "wrap" }}
          >
            {item.tags.map((tag) => (
              <Chip
                key={tag.id}
                size="small"
                variant="outlined"
                label={tag.title}
              />
            ))}
          </Stack>
        )}
      </Box>

      {!isMobile && (
        <Box sx={{ flexShrink: 0, minWidth: 130 }}>
          {isLive && (
            <Typography variant="body2" color="text.primary">
              {t("streams.form.viewersOnline", { count: item.viewersOnline })}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            {t("streams.item.viewsCount", { count: item.totalViews })}
          </Typography>
          {item.countryAccess && (
            <Typography variant="body2" color="text.secondary">
              {item.countryAccess.title}
            </Typography>
          )}
        </Box>
      )}

      {!isMobile && (
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            justifyContent: "flex-end",
          }}
        >
          {item.stream?.finishAt ? (
            <Box>
              <Typography variant="body2" color="text.primary" align="right">
                {t("streams.item.finished", {
                  finishAt: item.stream.finishAt,
                })}
              </Typography>
              <Typography variant="body2" color="text.secondary" align="right">
                {t("streams.item.finishedDate", {
                  finishAt: item.stream.finishAt,
                })}
              </Typography>
            </Box>
          ) : item.stream?.startAt ? (
            <Box>
              <Typography variant="body2" color="text.primary" align="right">
                {t("streams.item.willStartAt", {
                  startAt: item.stream.startAt,
                })}
              </Typography>
              <Typography variant="body2" color="text.secondary" align="right">
                {t("streams.item.willStartAtDate", {
                  startAt: item.stream.startAt,
                })}
              </Typography>
            </Box>
          ) : item.publishAt ? (
            <Box>
              <Typography variant="body2" color="text.primary" align="right">
                {t("streams.item.publishedAtTime", {
                  publishAt: item.publishAt,
                })}
              </Typography>
              <Typography variant="body2" color="text.secondary" align="right">
                {t("streams.item.publishedAtDate", {
                  publishAt: item.publishAt,
                })}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.primary" align="right">
              {t("streams.item.notPublished")}
            </Typography>
          )}

          {item.publishAt && item.url && (
            <Tooltip title={t("streams.item.openTooltip")}>
              <IconButton
                target="_blank"
                href={item.url}
                size="large"
                onClick={(e) => e.stopPropagation()}
              >
                <OpenInNewIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
    </Card>
  );
};
